import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, CheckCircle, XCircle, Save } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import {
  useTimesheet,
  useSubmitTimesheet,
  useApproveTimesheet,
  useRejectTimesheet,
  useSetManualTimesheetEntries,
  useSetTimesheetDayRateChoice,
} from '@/hooks/use-payroll';
import { usePermissions } from '@/hooks/use-permissions';
import { ApprovalError, SelfApprovedBadge, useSelfApproval } from '@/app/components/payroll/self-approval';
import { usePlatformModules } from '@/hooks/use-modules';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';
import { formatMoney, useOrgCurrency } from '@/lib/currency';
import type { TimesheetDayRate, TimesheetDayRateJob } from '@/lib/types';

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  submitted: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

const RATE_SOURCE_LABELS: Record<TimesheetDayRateJob['rate_source'], string> = {
  worker_on_job: 'Worker on this job',
  job: 'Job rate',
  worker_default: 'Worker default',
  missing: 'Missing rate',
  chosen: 'Chosen',
  custom: 'Custom',
};

function formatLocalDate(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function dayRateChoiceText(day: TimesheetDayRate, currency: string): string {
  if (day.choice?.choice === 'job') {
    return `Chosen: ${day.choice.project_name ?? 'Job'}`;
  }
  if (day.choice?.choice === 'custom') {
    const reason = day.choice.reason ? ` — ${day.choice.reason}` : '';
    return `Custom: ${formatMoney(day.choice.custom_rate, currency)}${reason}`;
  }
  return 'Split by hours (default)';
}

export function TimesheetDetailPage() {
  const timeZone = useOrgTimeZone();
  const orgCurrency = useOrgCurrency();
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const isManager = permissions.canManagePayroll;

  const { data: timesheet, isLoading } = useTimesheet(Number(id));
  const submitTimesheet = useSubmitTimesheet();
  const approveTimesheet = useApproveTimesheet();
  const rejectTimesheet = useRejectTimesheet();
  const setDayRateChoice = useSetTimesheetDayRateChoice();

  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const selfApproval = useSelfApproval('timesheet');
  const [skippedEntryErrors, setSkippedEntryErrors] = useState<string[]>([]);
  const [dayRateDrafts, setDayRateDrafts] = useState<
    Record<string, { selection: string; customRate: string; reason: string }>
  >({});
  const [dayRateError, setDayRateError] = useState<string | null>(null);

  const { isModuleEnabled } = usePlatformModules();
  const projectsEnabled = isModuleEnabled('projects');
  const setManualEntries = useSetManualTimesheetEntries();

  useEffect(() => {
    if (!timesheet) return;
    setDayRateDrafts(
      Object.fromEntries(
        (timesheet.day_rates ?? []).map((day) => [
          day.date,
          {
            selection:
              day.choice?.choice === 'job'
                ? `job:${day.choice.project_id}`
                : day.choice?.choice === 'custom'
                  ? 'custom'
                  : 'split',
            customRate: day.choice?.custom_rate ?? '',
            reason: day.choice?.reason ?? '',
          },
        ])
      )
    );
  }, [timesheet?.day_rates]);

  const startDate = useMemo(() => {
    if (!timesheet?.week_starting) return null;
    return new Date(timesheet.week_starting + 'T00:00:00');
  }, [timesheet?.week_starting]);

  const dayLabels = useMemo(() => {
    if (!startDate) return [];
    const labels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const startDay = startDate.getDay();
    return Array.from({ length: 7 }, (_, i) => labels[(startDay + i) % 7]);
  }, [startDate]);

  const weekDates = useMemo(() => {
    if (!startDate) return [];
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(startDate);
      d.setDate(startDate.getDate() + i);
      return formatLocalDate(d);
    });
  }, [startDate]);

  const [grid, setGrid] = useState<{ date: string; hours: string; work_state: string }[]>([]);

  useEffect(() => {
    if (weekDates.length === 0) return;
    const existingMap = new Map((timesheet?.manual_entries ?? []).map((e) => [e.date, e]));
    setGrid(
      weekDates.map((date) => {
        const existing = existingMap.get(date);
        return {
          date,
          hours: existing ? String(existing.hours) : '0',
          work_state: existing?.work_state ?? '',
        };
      })
    );
  }, [timesheet?.manual_entries, weekDates]);

  const handleHoursChange = (date: string, value: string) => {
    setGrid((prev) => prev.map((row) => (row.date === date ? { ...row, hours: value } : row)));
  };

  const handleWorkStateChange = (date: string, value: string) => {
    setGrid((prev) =>
      prev.map((row) =>
        row.date === date ? { ...row, work_state: value.toUpperCase().slice(0, 2) } : row
      )
    );
  };

  if (isLoading) {
    return (
      <div className="p-8 text-center text-gray-500">Loading timesheet...</div>
    );
  }

  if (!timesheet) {
    return (
      <div className="p-8 text-center text-gray-500">Timesheet not found</div>
    );
  }

  const handleSubmit = () => {
    submitTimesheet.mutate(timesheet.id);
  };

  const handleApprove = () => {
    setSkippedEntryErrors([]);
    return selfApproval.approve(timesheet.id, async (confirmSelfApproval) => {
      const result = await approveTimesheet.mutateAsync({ id: timesheet.id, confirmSelfApproval });
      const skipped = result.skipped_time_entries ?? [];
      setSkippedEntryErrors([...new Set(skipped.map((entry) => entry.error))]);
    });
  };

  const handleReject = () => {
    if (!rejectReason.trim()) return;
    rejectTimesheet.mutate(
      { id: timesheet.id, reason: rejectReason },
      {
        onSuccess: () => {
          setShowRejectForm(false);
          setRejectReason('');
        },
      }
    );
  };

  const handleSaveManualEntries = () => {
    const entries = grid.map((row) => ({
      date: row.date,
      hours: row.hours === '' ? '0' : row.hours,
      work_state: row.work_state || null,
    }));
    setManualEntries.mutate({ id: timesheet.id, entries });
  };

  const handleDayRateChange = (
    workDate: string,
    field: 'selection' | 'customRate' | 'reason',
    value: string
  ) => {
    setDayRateError(null);
    setDayRateDrafts((current) => ({
      ...current,
      [workDate]: {
        selection: current[workDate]?.selection ?? 'split',
        customRate: current[workDate]?.customRate ?? '',
        reason: current[workDate]?.reason ?? '',
        [field]: value,
      },
    }));
  };

  const handleSaveDayRateChoice = async (day: TimesheetDayRate) => {
    const draft = dayRateDrafts[day.date] ?? {
      selection: 'split',
      customRate: '',
      reason: '',
    };
    setDayRateError(null);
    let payload: {
      work_date: string;
      choice: 'job' | 'split' | 'custom';
      project?: number;
      custom_rate?: string;
      reason?: string;
    };
    if (draft.selection === 'split') {
      payload = { work_date: day.date, choice: 'split' };
    } else if (draft.selection === 'custom') {
      const rate = Number(draft.customRate);
      if (!draft.customRate.trim() || !Number.isFinite(rate) || rate <= 0) {
        setDayRateError('Enter a custom rate greater than zero.');
        return;
      }
      if (!draft.reason.trim()) {
        setDayRateError('A reason is required for a custom rate.');
        return;
      }
      payload = {
        work_date: day.date,
        choice: 'custom',
        custom_rate: draft.customRate.trim(),
        reason: draft.reason.trim(),
      };
    } else {
      const projectId = Number(draft.selection.slice('job:'.length));
      if (!Number.isInteger(projectId) || !day.options.some((option) => option.project_id === projectId)) {
        setDayRateError('Choose a job offered for this date.');
        return;
      }
      payload = { work_date: day.date, choice: 'job', project: projectId };
    }

    await selfApproval.approve(timesheet.id, async (confirmSelfApproval) => {
      await setDayRateChoice.mutateAsync({
        id: timesheet.id,
        ...payload,
        confirm_self_approval: confirmSelfApproval,
      });
    });
  };

  const weekEnd = startDate ? new Date(startDate) : null;
  if (weekEnd) weekEnd.setDate(weekEnd.getDate() + 6);
  const weekEndStr = weekEnd ? formatLocalDate(weekEnd) : '';
  // An empty list means no entries, which the backend treats as manual.
  const hoursSource = timesheet.daily_hours[0]?.source ?? 'manual';
  const isOwn = user?.person_id != null && timesheet.person === user.person_id;
  const canEditManual =
    hoursSource === 'manual' &&
    timesheet.status === 'draft' &&
    isOwn &&
    !projectsEnabled;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/payroll/timesheets')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
      </div>

      {/* Status Banner */}
      {timesheet.status === 'rejected' && timesheet.rejection_reason && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm font-medium text-red-800">Rejected</p>
          <p className="text-sm text-red-600 mt-1">{timesheet.rejection_reason}</p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{timesheet.person_name ?? timesheet.tech_name ?? 'Unknown'}</h1>
            <Badge className={statusColors[timesheet.status] ?? 'bg-gray-100 text-gray-800'}>
              {timesheet.status}
            </Badge>
            <SelfApprovedBadge selfApproved={timesheet.self_approved} />
          </div>
          <p className="text-gray-600 mt-1">
            Week of {timesheet.week_starting} &mdash; {weekEndStr}
          </p>
        </div>
        <div className="flex gap-2">
          {timesheet.status === 'draft' && (
            <Button onClick={handleSubmit} disabled={submitTimesheet.isPending}>
              <Send className="h-4 w-4 mr-2" />
              Submit for Approval
            </Button>
          )}
          {isManager && timesheet.status === 'submitted' && (
            <>
              <Button
                variant="outline"
                className="text-green-600"
                onClick={handleApprove}
                disabled={approveTimesheet.isPending}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Approve
              </Button>
              <Button
                variant="outline"
                className="text-red-600"
                onClick={() => setShowRejectForm(true)}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Reject
              </Button>
            </>
          )}
        </div>
      </div>

      <ApprovalError
        message={dayRateError ?? selfApproval.errorFor(timesheet.id)}
      />
      {skippedEntryErrors.map((message) => (
        <ApprovalError key={message} message={message} />
      ))}
      {selfApproval.dialog}

      {showRejectForm && (
        <Card>
          <CardContent className="pt-4">
            <div id="timesheet-reject-fields" className="flex gap-2">
              <Input
                placeholder="Enter rejection reason..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="flex-1"
                required
              />
              <Button
                variant="destructive"
                data-validate-required="true"
                data-validation-scope="timesheet-reject-fields"
                onClick={handleReject}
                disabled={rejectTimesheet.isPending}
              >
                Confirm Reject
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowRejectForm(false);
                  setRejectReason('');
                }}
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Hours Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Total Hours</p>
            <p className="text-3xl font-bold text-gray-900">{timesheet.total_hours}h</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Regular Hours</p>
            <p className="text-3xl font-bold text-gray-900">{timesheet.total_regular_hours}h</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Overtime Hours</p>
            <p className="text-3xl font-bold text-orange-600">{timesheet.total_overtime_hours}h</p>
          </CardContent>
        </Card>
      </div>

      {canEditManual ? (
        <Card>
          <CardHeader>
            <CardTitle>Manual Hours</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <>
              <p className="text-sm text-gray-500">
                Enter daily hours for this week. Work state is optional and used for state-specific overtime rules.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {grid.map((row, idx) => (
                  <div key={row.date} className="space-y-2">
                    <Label className="text-xs text-gray-600">
                      {dayLabels[idx]} {row.date}
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      max="24"
                      step="0.01"
                      value={row.hours}
                      onChange={(e) => handleHoursChange(row.date, e.target.value)}
                      placeholder="0.00"
                    />
                    <Input
                      placeholder="State"
                      maxLength={2}
                      value={row.work_state}
                      onChange={(e) => handleWorkStateChange(row.date, e.target.value)}
                    />
                  </div>
                ))}
              </div>
              <div className="flex justify-end">
                <Button
                  onClick={handleSaveManualEntries}
                  disabled={setManualEntries.isPending}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Hours
                </Button>
              </div>
            </>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Daily Hours</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {timesheet.daily_hours.length === 0 ? (
              <p className="text-sm text-gray-500">
                No hours recorded for this week.
              </p>
            ) : (
              <div className="divide-y divide-gray-200">
                <div className="grid grid-cols-3 gap-4 py-2 text-sm font-medium text-gray-500">
                  <span>Day</span>
                  <span>Hours</span>
                  <span>Overtime</span>
                </div>
                {timesheet.daily_hours.map((row) => {
                  const dayIndex = weekDates.indexOf(row.date);
                  return (
                    <div
                      key={row.date}
                      className="grid grid-cols-3 gap-4 py-2"
                    >
                      <span className="text-sm text-gray-600">
                        {dayLabels[dayIndex] ?? ''} {row.date}
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        {row.hours}h
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        {row.overtime_hours}h
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex items-center justify-between border-t border-gray-200 pt-3 font-semibold">
              <span className="text-sm text-gray-700">Total</span>
              <span className="text-sm text-gray-900">{timesheet.total_hours}h</span>
            </div>
            <p className="text-sm text-gray-500">
              {hoursSource === 'time_entry'
                ? "From job time entries — edit on the job's Time tab."
                : 'From manual entries.'}
            </p>
          </CardContent>
        </Card>
      )}

      {timesheet.cost_code_breakdown.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Cost Code Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="divide-y divide-gray-200">
            {timesheet.cost_code_breakdown.map((row) => (
              <div
                key={row.id}
                className="flex items-center justify-between py-2"
              >
                <span className="text-sm text-gray-700">
                  {row.cost_code === null
                    ? 'No cost code'
                    : `${row.cost_code_code} — ${row.cost_code_name}`}
                </span>
                <span className="text-sm font-medium text-gray-900">
                  {row.hours}h
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {(timesheet.day_rates ?? []).some((day) =>
        day.jobs.some((job) => job.project_id !== null)
      ) && (
        <Card>
          <CardHeader>
            <CardTitle>Day rates by job</CardTitle>
          </CardHeader>
          <CardContent className="divide-y divide-gray-200">
            {(timesheet.day_rates ?? [])
              .filter((day) => day.jobs.some((job) => job.project_id !== null))
              .map((day) => {
                const draft = dayRateDrafts[day.date] ?? {
                  selection: 'split',
                  customRate: '',
                  reason: '',
                };
                const canEditChoice =
                  isManager && timesheet.status !== 'approved' && day.qualifies;
                return (
                  <section key={day.date} className="space-y-3 py-4">
                    <h3 className="text-sm font-semibold text-gray-900">{day.date}</h3>
                    <div className="space-y-2">
                      {day.jobs.map((job, index) => (
                        <div
                          key={`${job.project_id ?? 'none'}-${job.rate_type}-${index}`}
                          className="flex flex-wrap items-center justify-between gap-2 text-sm"
                        >
                          <span className="text-gray-700">{job.project_name}</span>
                          <span className="text-gray-600">
                            {job.project_name} — {job.rate_type} —{' '}
                            {formatMoney(job.rate, orgCurrency)} / {job.rate_type === 'daily' ? 'day' : 'hour'} (
                            {job.rate_source_label ?? RATE_SOURCE_LABELS[job.rate_source]}) —{' '}
                            {formatMoney(job.pay, orgCurrency)}
                          </span>
                        </div>
                      ))}
                    </div>
                    {canEditChoice ? (
                      <div className="space-y-3 rounded-md border border-gray-200 bg-gray-50 p-3">
                        <div>
                          <Label htmlFor={`day-rate-choice-${day.date}`}>Rate choice</Label>
                          <select
                            id={`day-rate-choice-${day.date}`}
                            className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={draft.selection}
                            onChange={(event) =>
                              handleDayRateChange(day.date, 'selection', event.target.value)
                            }
                          >
                            <option value="split">Split by hours (default)</option>
                            {day.options.map((option) => (
                              <option key={option.project_id} value={`job:${option.project_id}`}>
                                {option.project_name} rate ({formatMoney(option.day_rate, orgCurrency)})
                              </option>
                            ))}
                            <option value="custom">Custom…</option>
                          </select>
                        </div>
                        {draft.selection === 'custom' && (
                          <>
                            <div>
                              <Label htmlFor={`day-rate-custom-${day.date}`}>Custom daily rate</Label>
                              <Input
                                id={`day-rate-custom-${day.date}`}
                                type="number"
                                step="0.01"
                                value={draft.customRate}
                                onChange={(event) =>
                                  handleDayRateChange(day.date, 'customRate', event.target.value)
                                }
                              />
                            </div>
                            <div>
                              <Label htmlFor={`day-rate-reason-${day.date}`}>Reason</Label>
                              <Input
                                id={`day-rate-reason-${day.date}`}
                                value={draft.reason}
                                onChange={(event) =>
                                  handleDayRateChange(day.date, 'reason', event.target.value)
                                }
                              />
                            </div>
                          </>
                        )}
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-xs text-gray-500">
                            Current choice: {dayRateChoiceText(day, orgCurrency)}
                          </span>
                          <Button
                            onClick={() => void handleSaveDayRateChoice(day)}
                            disabled={setDayRateChoice.isPending}
                          >
                            <Save className="mr-2 h-4 w-4" />
                            Save rate choice
                          </Button>
                        </div>
                      </div>
                    ) : day.qualifies ? (
                      <p className="text-sm text-gray-600">
                        {dayRateChoiceText(day, orgCurrency)}
                      </p>
                    ) : null}
                  </section>
                );
              })}
          </CardContent>
        </Card>
      )}

      {/* Details */}
      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <dt className="text-sm text-gray-500">Worker</dt>
              <dd className="text-sm font-medium text-gray-900">{timesheet.person_name ?? timesheet.tech_name ?? 'Unknown'}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Week Starting</dt>
              <dd className="text-sm font-medium text-gray-900">{timesheet.week_starting}</dd>
            </div>
            {timesheet.submitted_at && (
              <div>
                <dt className="text-sm text-gray-500">Submitted At</dt>
                <dd className="text-sm font-medium text-gray-900">
                  {formatDateTimeInTimeZone(timesheet.submitted_at, timeZone)}
                </dd>
              </div>
            )}
            {timesheet.approved_by_username && (
              <div>
                <dt className="text-sm text-gray-500">Approved By</dt>
                <dd className="text-sm font-medium text-gray-900">
                  {timesheet.approved_by_username}
                </dd>
              </div>
            )}
            {timesheet.approved_at && (
              <div>
                <dt className="text-sm text-gray-500">Approved At</dt>
                <dd className="text-sm font-medium text-gray-900">
                  {formatDateTimeInTimeZone(timesheet.approved_at, timeZone)}
                </dd>
              </div>
            )}
            {timesheet.notes && (
              <div className="sm:col-span-2">
                <dt className="text-sm text-gray-500">Notes</dt>
                <dd className="text-sm font-medium text-gray-900">{timesheet.notes}</dd>
              </div>
            )}
          </dl>
        </CardContent>
      </Card>

      {/* Approval History */}
      {timesheet.approvals.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Approval History</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-200">
              {timesheet.approvals.map((approval) => (
                <div key={approval.id} className="flex items-center justify-between p-4">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {approval.action.charAt(0).toUpperCase() + approval.action.slice(1)}
                    </p>
                    <p className="text-xs text-gray-500">
                      by {approval.performed_by_username} &middot;{' '}
                      {formatDateTimeInTimeZone(approval.created_at, timeZone)}
                    </p>
                    {approval.notes && (
                      <p className="text-sm text-gray-600 mt-1">{approval.notes}</p>
                    )}
                  </div>
                  <Badge
                    className={
                      statusColors[approval.action] ?? 'bg-gray-100 text-gray-800'
                    }
                  >
                    {approval.action}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
